import { Col, Form, Row } from 'antd';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import { ScopeButton } from '../components/buttons';
import { FormInput } from '../components/formInputs';
import { FormSelect } from '../components/formSelect';
import type { AccountCountry, BillingDetails } from './types';

const CURRENCIES = ['EUR', 'GBP', 'USD', 'AUD', 'CAD', 'NZD', 'ZAR'] as const;

type BillingFormProps = {
  billingForm: FormInstance;
  countries: AccountCountry[];
  isLoading?: boolean;
  isAllowed?: boolean;
  saveBillingDetails: (details: BillingDetails) => void;
};

export const BillingForm = ({
  billingForm,
  countries,
  isLoading,
  isAllowed,
  saveBillingDetails,
}: BillingFormProps) => {
  const { t } = useTranslation();

  return (
    <Form
      layout="vertical"
      form={billingForm}
      className="account-billing-form"
      onFinish={(values: BillingDetails) =>
        saveBillingDetails({
          ...values,
          state: values.town,
        })
      }
    >
      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Form.Item name="companyName" label={t('signup.company-name')}>
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="addressLine1"
            label={t('signup.address-line1')}
            rules={[{ required: true, message: t('common.required-field') }]}
          >
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="addressLine2" label={t('signup.address-line2')}>
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="town"
            label={t('signup.town')}
            rules={[{ required: true, message: t('common.required-field') }]}
          >
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="zipCode"
            label={t('signup.zip-code')}
            rules={[{ required: true, message: t('common.required-field') }]}
          >
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="country"
            label={t('common.country')}
            rules={[{ required: true, message: t('common.required-field') }]}
          >
            <FormSelect
              width="100%"
              showSearch
              options={countries.map((country) => ({
                value: country.iso,
                label: country.name,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="currency"
            label={t('common.currency')}
            rules={[{ required: true, message: t('common.required-field') }]}
          >
            <FormSelect
              width="100%"
              options={CURRENCIES.map((curr) => ({ value: curr, label: curr }))}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="email"
            label={t('signup.email-address')}
            rules={[{ type: 'email', message: t('signup.invalid-email') }]}
          >
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="vatNumber"
            label={t('users.billing-vat-number', {
              defaultValue: t('users.advanced-rights.vat-number-checker'),
            })}
          >
            <FormInput width="100%" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <ScopeButton
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={!isAllowed}
            width="154px"
            height="32px"
          >
            {t('common.save')}
          </ScopeButton>
        </Col>
      </Row>
    </Form>
  );
};
